import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

function CookiePolicy() {
  return (
    <div className="py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold text-center mb-8">Cookie Policy</h1>
        
        <Card>
          <CardHeader>
            <CardTitle>Cookie Policy for Hill Bros Brighton Ltd</CardTitle>
            <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()}</p>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none">
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">1. What Are Cookies</h2>
              <p className="mb-3">
                Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and provide information to website owners.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">2. How We Use Cookies</h2>
              <p className="mb-3">
                Hill Bros Brighton Ltd website (www.hill-bros.co.uk) uses cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 mb-3">
                <li>Measure the effectiveness of our advertising campaigns</li>
                <li>Remember your cookie consent preferences</li>
                <li>Ensure the website functions correctly</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">3. Types of Cookies We Use</h2>
              
              <h3 className="text-lg font-medium mb-2">3.1 Strictly Necessary Cookies</h3>
              <p className="mb-3">
                These cookies are essential for the website to function properly. They enable core functionality such as security and accessibility.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Cookie</th>
                      <th className="text-left py-2">Purpose</th>
                      <th className="text-left py-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2">cookie-consent</td>
                      <td className="py-2">Stores your cookie consent preference</td>
                      <td className="py-2">1 year</td>
                    </tr>
                    <tr>
                      <td className="py-2">ARRAffinity</td>
                      <td className="py-2">Azure hosting session management</td>
                      <td className="py-2">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-medium mb-2">3.2 Marketing/Advertising Cookies</h3>
              <p className="mb-3">
                We use Google Ads cookies to measure the effectiveness of our advertising campaigns. These cookies are only set if you consent to marketing cookies.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Cookie</th>
                      <th className="text-left py-2">Purpose</th>
                      <th className="text-left py-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-2">_gcl_au</td>
                      <td className="py-2">Google Ads conversion tracking</td>
                      <td className="py-2">90 days</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2">_gcl_aw</td>
                      <td className="py-2">Stores ad click information</td>
                      <td className="py-2">90 days</td>
                    </tr>
                    <tr>
                      <td className="py-2">_gac_*</td>
                      <td className="py-2">Campaign-related information</td>
                      <td className="py-2">90 days</td>
                    </tr>
                  </tbody>
                </table>
                <p className="text-sm mt-3 text-muted-foreground">
                  These cookies are set by Google (google.com/ads). For more information, see{' '}
                  <a href="https://policies.google.com/technologies/cookies" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                    Google's cookie policy
                  </a>.
                </p>
              </div>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">4. Third-Party Services</h2>
              <p className="mb-3">
                Our website uses the following third-party services:
              </p>
              <ul className="list-disc pl-6 mb-3">
                <li><strong>Microsoft Azure</strong> - Website hosting (may set session cookies)</li>
                <li><strong>Google Fonts</strong> - Font delivery service (no cookies, but connects to Google servers)</li>
                <li><strong>Google Ads</strong> - Advertising measurement (sets cookies with your consent)</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">5. Your Cookie Choices</h2>
              
              <h3 className="text-lg font-medium mb-2">5.1 Browser Settings</h3>
              <p className="mb-3">
                Most web browsers allow you to control cookies through their settings. You can:
              </p>
              <ul className="list-disc pl-6 mb-3">
                <li>View what cookies are stored on your device</li>
                <li>Delete some or all cookies</li>
                <li>Block cookies from specific or all websites</li>
                <li>Set your browser to alert you when cookies are being set</li>
              </ul>

              <h3 className="text-lg font-medium mb-2">5.2 How to Manage Cookies in Popular Browsers</h3>
              <ul className="list-disc pl-6 mb-3">
                <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                <li><strong>Edge:</strong> Settings → Privacy, search, and services → Cookies and site permissions</li>
              </ul>

              <h3 className="text-lg font-medium mb-2">5.3 Impact of Disabling Cookies</h3>
              <p className="mb-3">
                Please note that disabling cookies may impact the functionality of our website. Some features may not work properly without cookies.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">6. Consent</h2>
              <p className="mb-3">
                When you first visit our website, you will see a cookie consent banner. Strictly necessary cookies are used without consent as they are essential for the website to function. Marketing cookies (Google Ads) are only set if you click "Accept" on the cookie banner.
              </p>
              <p className="mb-3">
                You can withdraw your consent at any time by clearing your browser cookies and revisiting the site, or by adjusting your browser settings to block cookies from our domain.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">7. Cookie Duration</h2>
              <p className="mb-3">
                The cookies used on our website have the following durations:
              </p>
              <ul className="list-disc pl-6 mb-3">
                <li><strong>Cookie consent preference:</strong> 1 year</li>
                <li><strong>Azure session cookies:</strong> Deleted when you close your browser</li>
                <li><strong>Google Ads cookies:</strong> Up to 90 days</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">8. Updates to This Policy</h2>
              <p className="mb-3">
                We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. The updated policy will be posted on this page with a new "Last updated" date.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">9. More Information About Cookies</h2>
              <p className="mb-3">
                For more general information about cookies, you can visit:
              </p>
              <ul className="list-disc pl-6 mb-3">
                <li><a href="https://ico.org.uk/for-the-public/online/cookies/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Information Commissioner's Office (ICO) - Cookies</a></li>
                <li><a href="https://www.allaboutcookies.org/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">All About Cookies</a></li>
                <li><a href="https://www.youronlinechoices.com/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">Your Online Choices</a></li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">10. Contact Us</h2>
              <p className="mb-3">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold">Hill Bros Brighton Ltd</p>
                <p>31 Connaught Road</p>
                <p>Seaford, BN25 2PU</p>
                <p>Email: info@hill-bros.co.uk</p>
                <p>Phone: 01273 326200</p>
              </div>
            </section>

          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default CookiePolicy